// OnboardingWizard — slim container that composes the individual step
// components. All step JSX lives in `./onboarding/steps/*` — this file only
// owns navigation state, profile state, and the submit flow.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { api } from "@/lib/api";
import { ONBOARDING_STEPS, canProceedFromStep } from "./onboarding/config";
import WelcomeStep from "./onboarding/steps/WelcomeStep";
import ResumeStep from "./onboarding/steps/ResumeStep";
import RoleStep from "./onboarding/steps/RoleStep";
import LocationStep from "./onboarding/steps/LocationStep";
import DobStep from "./onboarding/steps/DobStep";
import GoalsStep from "./onboarding/steps/GoalsStep";
import SalaryStep from "./onboarding/steps/SalaryStep";
import DoneStep from "./onboarding/steps/DoneStep";

export default function OnboardingWizard({ onClose, onDone }) {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState({});
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/profile").then((r) => setProfile(r.data || {})).catch(() => {});
  }, []);

  const progressPct = Math.round((step / (ONBOARDING_STEPS.length - 1)) * 100);
  const s = ONBOARDING_STEPS[step];
  const saveField = (patch) => setProfile((p) => ({ ...p, ...patch }));

  const uploadResume = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/resume/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile(data.profile || {});
      toast.success("Resume parsed successfully");
      setStep(step + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const finish = async () => {
    setSaving(true);
    try {
      const payload = {
        name: profile.name,
        current_role: profile.current_role,
        location: profile.location,
        country: profile.country || null,
        country_code: profile.country_code || null,
        region: profile.region || null,
        city: profile.city || null,
        preferred_currency: profile.preferred_currency || null,
        timezone: profile.timezone || null,
        current_salary: Number(profile.current_salary) || null,
        target_salary: Number(profile.target_salary) || null,
        career_goals: profile.career_goals || [],
      };
      await api.put("/profile", payload);
      await api.post("/insights/generate").catch(() => {});
      toast.success("Welcome aboard!");
      onDone();
      nav("/app");
    } catch (err) {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const canProceed = canProceedFromStep(s.key, profile);

  const renderStep = () => {
    switch (s.key) {
      case "welcome":  return <WelcomeStep />;
      case "resume":   return <ResumeStep uploading={uploading} onUpload={uploadResume} onSkip={() => setStep(step + 1)} />;
      case "role":     return <RoleStep     profile={profile} saveField={saveField} />;
      case "location": return <LocationStep profile={profile} saveField={saveField} />;
      case "dob":      return <DobStep      profile={profile} saveField={saveField} />;
      case "goals":    return <GoalsStep    profile={profile} saveField={saveField} />;
      case "salary":   return <SalaryStep   profile={profile} saveField={saveField} />;
      case "done":     return <DoneStep />;
      default: return null;
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl p-0 gap-0 overflow-hidden border-border"
        data-testid="onboarding-wizard"
      >
        <VisuallyHidden>
          <DialogTitle>Onboarding — {s.title}</DialogTitle>
          <DialogDescription>{s.subtitle}</DialogDescription>
        </VisuallyHidden>

        {/* Progress bar */}
        <div className="h-1 bg-secondary relative">
          <motion.div
            className="h-full bg-primary"
            initial={{ width: 0 }}
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          />
        </div>

        <div className="p-8">
          <div className="flex items-start justify-between mb-6">
            <div className="pr-8">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                Step {step + 1} of {ONBOARDING_STEPS.length}
              </div>
              <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight text-foreground">
                {s.title}
              </h2>
              <p className="text-sm text-muted-foreground mt-2">{s.subtitle}</p>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              className="min-h-[220px]"
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>

          {/* Footer nav */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <Button
              variant="ghost"
              onClick={() => (step === 0 ? onClose() : setStep(step - 1))}
              disabled={saving}
              data-testid="onboarding-back"
              className="gap-2"
            >
              <ArrowLeft size={14} /> {step === 0 ? "Later" : "Back"}
            </Button>
            {step < ONBOARDING_STEPS.length - 1 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed || uploading}
                data-testid="onboarding-next"
                className="gap-2"
              >
                Continue <ArrowRight size={14} />
              </Button>
            ) : (
              <Button onClick={finish} disabled={saving} data-testid="onboarding-finish" className="gap-2">
                {saving ? "Saving…" : "Go to Dashboard"} <ArrowRight size={14} />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
